import { InjectedProperty } from "Global/Base/DIContainer";
import { Event } from "Global/Base/Events";
import { GlobalContext } from "Global/Base/GlobalContext";
import { IFRMParams } from "../Controls/FormParameters";
import { IOpenedFormsHistoryManager } from "../Helpers/IOpenedFormsHistoryManager";
import { IWindowable } from "../UIManagerInterfaces";
import { FormResult } from "../XGControls/Forms/FormResult";
import { IForm } from "../XGControls/IForm";
import { IModalWindowConfiguration, ModalWindowType } from "../XGControls/ModalWindow";
import { Homepage } from "./Homepage";
import { IFormGroup, IFormGroupOpenModalParams } from "./IFormGroup";
import { FormManagerProperty, ICustomTitleChangedInfo, IFavoriteFormChanged, IFavoriteFormItem, IFormProfileChanged, IHideCloseButtonChanged, IOpenedFormInfo } from "./IFormManagerInterfaces";

/**
 * Obsahuje informace o otevřeném formuláři, včetně odkazu na otevřený formulář a jeho související formuláře.
 */
export interface IOpenedForm {
    readonly formGroup: IFormGroup;
    readonly openedFormInfo: IOpenedFormInfo;
    readonly openInBackground: boolean;
}

export type IFavoriteChangedEvent = {
    formName: string;
    status: boolean;
};

/**
 * FormManager slouží ke kompletní správě otevřených formulářů v aplikaci
 *
 * @includeToDoc
 */
export interface IFormManager {
    /** Údalost volaná po zavření formuláře */
    readonly formClosed: Event<IFormManager, IOpenedForm>;
    /** Údalost volaná po otevření formuláře */
    readonly formOpened: Event<IFormManager, IOpenedForm>;
    /** Údalost volaná po přidání/odbrání formuláře do/z oblibených */
    readonly formFavoriteChanged: Event<IFormManager, IFavoriteChangedEvent>;
    /** Událost o změně customTitle některého otevřeného formuláře */
    readonly formCustomTitleChanged: Event <IFormManager, ICustomTitleChangedInfo>;
    /** Událost o změně stavu skrytí tlačítka pro zavření formuláře */
    readonly formHideCloseButtonChanged: Event<IFormManager, IHideCloseButtonChanged>;
    /** Událos volaná při přepnutí formuláře */
    readonly formSwitched: Event<IFormManager, IOpenedForm>;
     /** Událost vyvolaná při změně oblíbenosti formuláře */
    readonly favoriteFormSet: Event<IFormManager, IFavoriteFormChanged>;
    /** Událost vyvolaná při změně uživatelského profilu formuláře */
    readonly formProfileChanged: Event<IFormManager, IFormProfileChanged>;
    /** Udalost zavolana po inicializaci FormManageru */
    readonly initialized: Promise<IFormManager>;

    /** Seznam otevřených formulářů */
    readonly openedForms: ReadonlyArray<IOpenedForm>;
    /** Naposledy otevřený formulář */
    readonly lastOpenedForm: IOpenedForm | undefined;
    /** Naposledy aktivní formulář */
    readonly lastActiveForm: IOpenedForm | undefined;

    /** Instance spravující historii otevřených formulářů. */
    readonly openedFormsHistoryManager: IOpenedFormsHistoryManager;
    /** Vratí seznam všech formInstanceId formuláře v daném pořadí jakém byly aktivovány */
    readonly activityFormHistory: ReadonlyArray<string>;
    /** Informace o tom, zda je FormManager jiz nainicializovan */
    readonly isInitialized: boolean;
    /** Vraci promise na ktery se s posledni operaci otevirani formulare */
    readonly openingFormPromise: Promise<IOpenedForm>;

    /** Preview ID pod kterým se mají načítat upravené definice formuláře ze serveru */
    readonly previewId?: string;

    /** Nastaví vlastnost formuláře */
    setProperty(property: FormManagerProperty.favoriteForm, value: { menuId: number; formName: string; value: boolean }): Promise<void>;
    setProperty(property: FormManagerProperty, value: any): Promise<void>;

    /** Vrátí vlastnost formuláře */
    getProperty(property: FormManagerProperty.favoriteForm): Promise<IFavoriteFormItem[]>;
    getProperty(property: FormManagerProperty.favoriteForm, menuId: number): Promise<boolean>;
    getProperty(property: FormManagerProperty, args?: any): Promise<any>;
    /** Vrátí otevřený formulář pokud odpovidá vstupním parametrům jinak vratí undefined */
    getOpenedForm(formName: string, number: number): IOpenedForm | undefined;
    /**
     * Otevře formulář
     * @param formName Název formuláře
     * @param parametricData Parametry pro otevření formuláře
     * @param preferExistingForm Pokud je nastaveno na true tak se preferuje již otevřný formulář
     */
    openForm(formName: string, parametricData?: IFRMParams, preferExistingForm?: boolean): Promise<IOpenedForm>;
    /**
     * Otevře modální okno
     * @param formName Název formuláře
     * @param parametricData Parametry oteviraného formuláře
     * @param options Nastavení pro otevření modálu
     */
    openFormModal(options: IFormGroupOpenModalParams): Promise<IWindowable>;

    /**
     * Přepne se na otevřený formulář podle jeho identifikátoru
     * @param formInstanceId Unikátní identifikátor formuláře
     * @returns Vrací true pokud byl formulář s daným identifikátorem nalezen a bylo na něj přepnuto; jinak false
     */
    switchToForm(formInstanceId: string): boolean;

    /**
     * Otevře vlastní instanci podřízeného formuláře
     *
     * @param form Vlastní form, který má byt zobrazen
     * @param init Nepovinná inicializační metoda, které je předáno aktuální IFormUI
     */
    openCustomModalForm<TForm extends IWindowable>(form: TForm): Promise<TForm>;

    /**
     * Otevře vlastní instanci formuláře
     *
     * @param form Vlastní form, který má byt zobrazen
     * @param showInMenu Nepovinná parametr, zda se ma položka zobrazit v menu či nikoli, výchozi hodnota je true
     */
    openCustomForm<TForm extends IWindowable>(form: TForm, showInMenu?: boolean): Promise<IOpenedForm>;

    /** Zavře všechny formuláře, v případě, že má nějaký formulář rozeditované změny, zobrazí YNC dialog */
    closeAllForms(): Promise<boolean>;

    /** Zjistí, zda je některý z formulářů rozeditovaný. */
    isAnyFormEdited(): Promise<boolean>;

    /**
     * Zavře formulář s unikátním ID
     * @param {string} openedFormId Unikatní ID formuláře
     */
    closeForm(openedFormId: string): Promise<boolean>;

    /** Skryje vsechny formulare */
    hideAllForms(): void;

    /**
     * Nastaví standardní nebo vlastní programově vytvořený formulář jako úvodní stránku aplikace
     * @param form Kód formuláře nebo instance formuláře
     */
    setHomepage(form: IWindowable | IForm | string): Promise<Homepage>;

    /** Vytvoří formInstanceId pro otvíraný formulář se zadanými parametry. Aktualizuje čítač instancí.
     * @param formCode Kód formuláře.
     * @param formParameters Parametry pro otevření.
     */
    createFormInstanceId(formCode: string, formParameters?: IFRMParams): string;
}

export class IFormManager {
    private static _properties: (keyof(IFormManager))[] = [ "openingFormPromise", "initialized", "isInitialized", "formOpened", "favoriteFormSet", "formProfileChanged", "activityFormHistory", "openedFormsHistoryManager", "formClosed", "formFavoriteChanged", "openedForms", "lastOpenedForm", "lastActiveForm"];
    private static _methods: (keyof(IFormManager))[] = ["getProperty", "getOpenedForm", "openForm", "setProperty", "openFormModal", "closeForm", "openCustomModalForm", "hideAllForms", "setHomepage"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Nastavení pro zobrazení modálního okna
 * @includeToDoc
 */
export interface IShowModalParams {
    /** Okno, které má být zobrazeno */
    window: IWindowable;
    /** Typ modálního okna */
    type: ModalWindowType;
    // Nnadřízený HTML Element
    parent?: HTMLElement;
    /** Metoda provedá při zavření modálního okna */
    onClose?: (sender?: IWindowable, result?: FormResult) => void;
    /** Příznak, jestli se má zobrazit overlay vrstva */
    showOverlay?: boolean;
    /** Konfigurace modální okna */
    modalConfig?: IModalWindowConfiguration;
    /** Příznak jestli se má zobrazit v drobečkové navigaci */
    showBreadcrumbNav?: boolean;
}

/**
 * Struktura pro nastavení oteviraného modálu
 * @includeToDoc
 */
export interface IOpenModalParams {
    onClose?: (sender?: IWindowable, result?: FormResult) => void;
    type?: ModalWindowType;
    showBreadcrumbNav?: boolean;
    parametricData?: IFRMParams;
}

/**
 * Property sloužící pro přístup k UIManager
 */
export class FormManager {
    /**
     * Instance v kontextu současného okna
     */
    public static get instance(): IFormManager {
        return {} as any;
    }
}

InjectedProperty.bindProperty(FormManager, "instance", IFormManager);
GlobalContext.registerType(FormManager);
